import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/LegalLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ShieldAlert, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const categories = [
  "Item not received", "Item not as described", "Damaged or faulty item",
  "Refund not processed", "Seller misconduct", "Prohibited or unsafe product", "Other",
];

export default function DisputesReports() {
  useEffect(() => { document.title = "Disputes & Reports — Xurray"; }, []);
  const [form, setForm] = useState({ name: "", email: "", order_id: "", category: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Please choose a category.");
    setLoading(true);
    try {
      const { data } = await api.post("/support/dispute", form);
      setRef(data.ref);
      toast.success("Your report has been submitted.");
    } catch {
      toast.error("Could not submit your report. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Layout hideAd>
      <div data-testid="disputes-page">
        <PageHero
          title="Disputes & Reports"
          eyebrow="Trust & Safety"
          icon={ShieldAlert}
          intro="Raise an issue with an order, a seller, or a listing. Our compliance team reviews every valid report, usually within 48 hours."
        />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          {ref ? (
            <div className="rounded-2xl border border-green-500/30 bg-green-500/5 p-8 animate-fade-up" data-testid="disputes-success">
              <CheckCircle2 className="w-10 h-10 text-green-600 mb-3" />
              <h2 className="font-display font-bold text-xl">Report received</h2>
              <p className="text-sm text-muted-foreground mt-1">Your reference number is <strong className="text-foreground" data-testid="disputes-ref">{ref}</strong>. Keep it for your records — we'll email you with any updates.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8 space-y-5 animate-fade-up" data-testid="disputes-form">
              <div><Label htmlFor="d-name">Full name</Label><Input id="d-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="disputes-name" /></div>
              <div><Label htmlFor="d-email">Email</Label><Input id="d-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="disputes-email" /></div>
              <div><Label htmlFor="d-order">Order reference (optional)</Label><Input id="d-order" value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} className="mt-1.5" data-testid="disputes-order" /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="disputes-category"><SelectValue placeholder="Select a category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c} data-testid={`disputes-cat-${c}`}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label htmlFor="d-desc">Describe the issue</Label><Textarea id="d-desc" required rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="disputes-description" /></div>
              <Button type="submit" disabled={loading} size="lg" className="rounded-full w-full sm:w-auto" data-testid="disputes-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit report"}
              </Button>
            </form>
          )}
        </main>
      </div>
    </Layout>
  );
}
